import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Question, QuestionResponse } from "@/types/test-response";
import { calculateScorePercentage } from "@/utils/score-utils";

interface SkillScoreTableProps {
  questions: Question[];
  candidateQuestionResponses: QuestionResponse[];
}

interface SkillScore {
  skill: string;
  earnedPoints: number;
  totalPoints: number;
  percentage: number;
  questionCount: number;
}

export function SkillScoreTable({ questions, candidateQuestionResponses }: SkillScoreTableProps) {
  // Aggregate scores by skill
  const skillScores: Record<string, SkillScore> = {};

  questions.forEach((question) => {
    const skill = question.skill || "General";
    const questionResponse = candidateQuestionResponses.find(
      (qr) => qr.question_id === question.id
    );
    
    const earnedScore = questionResponse?.ai_feedback?.score || questionResponse?.score || 0;
    const maxScore = question.marks;

    if (!skillScores[skill]) {
      skillScores[skill] = {
        skill,
        earnedPoints: 0,
        totalPoints: 0,
        percentage: 0,
        questionCount: 0,
      };
    }

    skillScores[skill].earnedPoints += earnedScore;
    skillScores[skill].totalPoints += maxScore;
    skillScores[skill].questionCount += 1;
  });

  // Calculate percentages
  Object.values(skillScores).forEach((skillScore) => {
    skillScore.percentage = calculateScorePercentage(
      skillScore.earnedPoints,
      skillScore.totalPoints
    );
  });

  // Sort by percentage descending
  const sortedSkills = Object.values(skillScores).sort(
    (a, b) => b.percentage - a.percentage
  );

  const getScoreColor = (percentage: number) => {
    if (percentage >= 80) return "text-green-600 dark:text-green-400";
    if (percentage >= 60) return "text-yellow-600 dark:text-yellow-400";
    return "text-red-600 dark:text-red-400";
  };

  const getScoreBadgeVariant = (percentage: number) => {
    if (percentage >= 80) return "default";
    if (percentage >= 60) return "secondary";
    return "destructive";
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Skill Performance</CardTitle>
      </CardHeader>
      <CardContent>
        {sortedSkills.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No skill data available
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Skill</TableHead>
                <TableHead className="text-center">Questions</TableHead>
                <TableHead className="text-center">Score</TableHead>
                <TableHead className="text-center">Percentage</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedSkills.map((skillScore) => (
                <TableRow key={skillScore.skill}>
                  <TableCell className="font-medium">
                    {skillScore.skill}
                  </TableCell>
                  <TableCell className="text-center">
                    {skillScore.questionCount}
                  </TableCell>
                  <TableCell className="text-center">
                    <span className={getScoreColor(skillScore.percentage)}>
                      {skillScore.earnedPoints}/{skillScore.totalPoints}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={getScoreBadgeVariant(skillScore.percentage)}>
                      {skillScore.percentage}%
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}